import type { InvoiceItem, InvoiceItemFormData, TaxType, DiscountType } from '@/types';
import { v4 as uuidv4 } from 'uuid';

// ─── Item calculations ────────────────────────────────────────────────────────

export interface ItemTotals {
  taxableAmount: number;
  cgstAmount:    number;
  sgstAmount:    number;
  igstAmount:    number;
  amount:        number;
}

export const calculateItemTotals = (
  quantity: number,
  unitPrice: number,
  gstPercentage: number,
  taxType: TaxType,
): ItemTotals => {
  const taxableAmount = parseFloat((quantity * unitPrice).toFixed(2));
  let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

  if (taxType === 'cgst_sgst') {
    cgstAmount = parseFloat((taxableAmount * (gstPercentage / 2) / 100).toFixed(2));
    sgstAmount = parseFloat((taxableAmount * (gstPercentage / 2) / 100).toFixed(2));
  } else if (taxType === 'igst') {
    igstAmount = parseFloat((taxableAmount * gstPercentage / 100).toFixed(2));
  }

  const amount = parseFloat((taxableAmount + cgstAmount + sgstAmount + igstAmount).toFixed(2));
  return { taxableAmount, cgstAmount, sgstAmount, igstAmount, amount };
};

// ─── Invoice summary calculations ────────────────────────────────────────────

export interface InvoiceTotals {
  subtotal:        number;
  discountAmount:  number;
  taxableSubtotal: number;
  cgstAmount:      number;
  sgstAmount:      number;
  igstAmount:      number;
  totalTax:        number;
  grandTotal:      number;
}

export const calculateInvoiceTotals = (
  items: Array<{ taxableAmount: number; cgstAmount: number; sgstAmount: number; igstAmount: number; amount: number }>,
  discountType: DiscountType,
  discountValue: number,
  taxType: TaxType,
): InvoiceTotals => {
  const subtotal = parseFloat(items.reduce((s, i) => s + i.taxableAmount, 0).toFixed(2));

  let discountAmount = 0;
  if (discountType === 'fixed') {
    discountAmount = Math.min(discountValue, subtotal);
  } else {
    discountAmount = parseFloat((subtotal * discountValue / 100).toFixed(2));
  }

  const taxableSubtotal = parseFloat((subtotal - discountAmount).toFixed(2));

  // Re-calc taxes proportionally if there's a discount
  const discountRatio = subtotal > 0 ? taxableSubtotal / subtotal : 1;
  const cgstAmount   = parseFloat(items.reduce((s, i) => s + i.cgstAmount, 0).toFixed(2)) * discountRatio;
  const sgstAmount   = parseFloat(items.reduce((s, i) => s + i.sgstAmount, 0).toFixed(2)) * discountRatio;
  const igstAmount   = parseFloat(items.reduce((s, i) => s + i.igstAmount, 0).toFixed(2)) * discountRatio;

  const totalTax  = parseFloat((cgstAmount + sgstAmount + igstAmount).toFixed(2));
  const grandTotal = parseFloat((taxableSubtotal + totalTax).toFixed(2));

  return {
    subtotal,
    discountAmount: parseFloat(discountAmount.toFixed(2)),
    taxableSubtotal,
    cgstAmount: parseFloat(cgstAmount.toFixed(2)),
    sgstAmount: parseFloat(sgstAmount.toFixed(2)),
    igstAmount: parseFloat(igstAmount.toFixed(2)),
    totalTax,
    grandTotal,
  };
};

// ─── Amount in words ──────────────────────────────────────────────────────────

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const toWords = (num: number): string => {
  if (num === 0) return 'Zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + toWords(num % 100) : '');
  if (num < 100000) return toWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + toWords(num % 1000) : '');
  if (num < 10000000) return toWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + toWords(num % 100000) : '');
  return toWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + toWords(num % 10000000) : '');
};

export const amountToWords = (amount: number): string => {
  const rupees = Math.floor(amount);
  const paise  = Math.round((amount - rupees) * 100);
  let words    = 'Rupees ' + toWords(rupees);
  if (paise > 0) words += ' and ' + toWords(paise) + ' Paise';
  return words + ' Only';
};

// ─── Format currency ──────────────────────────────────────────────────────────

export const formatCurrency = (amount: number, currency = '₹'): string => {
  return `${currency}${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const newItemId = () => uuidv4();
